import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  CloneEditableReportVersionCommand,
  ExpectedRevision,
  Page,
  PageRequest,
  PersistedReport,
  PersistedReportSection,
  PersistedReportVersion,
  PersistedSectionRevision,
  ReportRepository,
  RepositoryResult,
} from "../contracts";
import type { Database } from "../database.generated";
import type {
  ReportRow,
  ReportSectionRow,
  ReportVersionRow,
  SectionWorkflowRevisionRow,
} from "../database.types";
import {
  mapReportRow,
  mapReportSectionRow,
  mapReportVersionRow,
  mapSectionWorkflowRevisionRow,
} from "../mappers";
import { decodeCursor, encodeCursor, type CursorScalar } from "./cursor";
import { isSha256 } from "./checksum";
import { reportMetadataCodec, reportVersionMetadataCodec, sectionWorkflowCodec } from "./codecs";
import { revisionConflict } from "./errors";
import { mapRepositoryRows, validatePageRequest } from "./repository-base";
import { databaseFailure, notFound, validationFailure } from "./repository-helpers";

export const REPORT_ORDER = "reporting_period.asc,id.asc";
export const VERSION_ORDER = "version_number.asc,id.asc";
export const SECTION_ORDER = "ordinal.asc,id.asc";
export const WORKFLOW_REVISION_ORDER = "revision_number.asc,id.asc";

const cursorValues = (
  page: PageRequest,
  order: string,
  types: Array<"string" | "number">,
): RepositoryResult<CursorScalar[] | undefined> => {
  const valid = validatePageRequest(page);
  if (!valid.ok) return valid as RepositoryResult<CursorScalar[] | undefined>;
  if (!page.cursor) return { ok: true, value: undefined };
  const decoded = decodeCursor(page.cursor, order, types.length);
  if (!decoded.ok) return decoded as RepositoryResult<CursorScalar[] | undefined>;
  if (decoded.value.values.some((value, index) => typeof value !== types[index])) {
    return validationFailure("The pagination cursor contains invalid ordering values.");
  }
  return { ok: true, value: decoded.value.values };
};

const quoteFilter = (value: string) => `"${value.replace(/\\/gu, "\\\\").replace(/"/gu, '\\"')}"`;

function createPage<Row, Value>(
  rows: readonly Row[],
  page: PageRequest,
  order: string,
  cursorFor: (row: Row) => CursorScalar[],
  mapper: (row: Row) => RepositoryResult<Value> | Value,
): RepositoryResult<Page<Value>> {
  const hasNext = rows.length > page.limit;
  const visible = hasNext ? rows.slice(0, page.limit) : [...rows];
  const mapped = mapRepositoryRows(visible, mapper);
  if (!mapped.ok) return mapped;
  return {
    ok: true,
    value: {
      items: mapped.value,
      nextCursor:
        hasNext && visible.length
          ? encodeCursor({ version: 1, order, values: cursorFor(visible[visible.length - 1]) })
          : undefined,
    },
  };
}

export class SupabaseReportRepository implements ReportRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getById(id: string): Promise<RepositoryResult<PersistedReport>> {
    const { data, error } = await this.client.from("reports").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "The report could not be read.");
    if (!data) return notFound("Report");
    return mapReportRow(data as ReportRow);
  }

  async getVersion(id: string): Promise<RepositoryResult<PersistedReportVersion>> {
    const { data, error } = await this.client.from("report_versions").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "The report version could not be read.");
    if (!data) return notFound("Report version");
    return mapReportVersionRow(data as ReportVersionRow);
  }

  async getSection(id: string): Promise<RepositoryResult<PersistedReportSection>> {
    const { data, error } = await this.client.from("report_sections").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "The report section could not be read.");
    if (!data) return notFound("Report section");
    return { ok: true, value: mapReportSectionRow(data as ReportSectionRow) };
  }

  async getSectionRevision(id: string): Promise<RepositoryResult<PersistedSectionRevision>> {
    const { data, error } = await this.client
      .from("section_workflow_revisions")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) return databaseFailure(error, "The section workflow revision could not be read.");
    if (!data) return notFound("Section workflow revision");
    return mapSectionWorkflowRevisionRow(data as SectionWorkflowRevisionRow);
  }

  async getCurrentSectionRevision(sectionId: string): Promise<RepositoryResult<PersistedSectionRevision>> {
    const section = await this.getSection(sectionId);
    if (!section.ok) return section;
    if (!section.value.currentWorkflowRevisionId) return notFound("Current section workflow revision");
    return this.getSectionRevision(section.value.currentWorkflowRevisionId);
  }

  listForOrganisation(organisationId: string, page: PageRequest) {
    return this.listReports({ organisationId }, page);
  }

  listForAudit(auditId: string, page: PageRequest) {
    return this.listReports({ auditId }, page);
  }

  private async listReports(
    filter: { organisationId?: string; auditId?: string },
    page: PageRequest,
  ): Promise<RepositoryResult<Page<PersistedReport>>> {
    const order = filter.organisationId
      ? `${REPORT_ORDER}|organisation:${filter.organisationId}`
      : `${REPORT_ORDER}|audit:${filter.auditId ?? ""}`;
    const cursor = cursorValues(page, order, ["string", "string"]);
    if (!cursor.ok) return cursor;
    let query = this.client
      .from("reports")
      .select("*")
      .order("reporting_period", { ascending: true })
      .order("id", { ascending: true })
      .limit(page.limit + 1);
    if (filter.organisationId) query = query.eq("organisation_id", filter.organisationId);
    if (filter.auditId) query = query.eq("audit_engagement_id", filter.auditId);
    if (cursor.value) {
      const [period, id] = cursor.value as [string, string];
      query = query.or(
        `reporting_period.gt.${quoteFilter(period)},and(reporting_period.eq.${quoteFilter(period)},id.gt.${quoteFilter(id)})`,
      );
    }
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Reports could not be listed.");
    return createPage(
      (data ?? []) as ReportRow[],
      page,
      order,
      (row) => [row.reporting_period, row.id],
      mapReportRow,
    );
  }

  async listVersions(reportId: string, page: PageRequest): Promise<RepositoryResult<Page<PersistedReportVersion>>> {
    const order = `${VERSION_ORDER}|report:${reportId}`;
    const cursor = cursorValues(page, order, ["number", "string"]);
    if (!cursor.ok) return cursor;
    let query = this.client
      .from("report_versions")
      .select("*")
      .eq("report_id", reportId)
      .order("version_number", { ascending: true })
      .order("id", { ascending: true })
      .limit(page.limit + 1);
    if (cursor.value) {
      const [number, id] = cursor.value as [number, string];
      query = query.or(`version_number.gt.${number},and(version_number.eq.${number},id.gt.${quoteFilter(id)})`);
    }
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Report versions could not be listed.");
    return createPage(
      (data ?? []) as ReportVersionRow[],
      page,
      order,
      (row) => [row.version_number, row.id],
      mapReportVersionRow,
    );
  }

  async listSections(
    reportVersionId: string,
    page: PageRequest,
  ): Promise<RepositoryResult<Page<PersistedReportSection>>> {
    const order = `${SECTION_ORDER}|version:${reportVersionId}`;
    const cursor = cursorValues(page, order, ["number", "string"]);
    if (!cursor.ok) return cursor;
    let query = this.client
      .from("report_sections")
      .select("*")
      .eq("report_version_id", reportVersionId)
      .order("ordinal", { ascending: true })
      .order("id", { ascending: true })
      .limit(page.limit + 1);
    if (cursor.value) {
      const [ordinal, id] = cursor.value as [number, string];
      query = query.or(`ordinal.gt.${ordinal},and(ordinal.eq.${ordinal},id.gt.${quoteFilter(id)})`);
    }
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Report sections could not be listed.");
    return createPage(
      (data ?? []) as ReportSectionRow[],
      page,
      order,
      (row) => [row.ordinal, row.id],
      mapReportSectionRow,
    );
  }

  async listSectionRevisions(
    reportSectionId: string,
    page: PageRequest,
  ): Promise<RepositoryResult<Page<PersistedSectionRevision>>> {
    const order = `${WORKFLOW_REVISION_ORDER}|section:${reportSectionId}`;
    const cursor = cursorValues(page, order, ["number", "string"]);
    if (!cursor.ok) return cursor;
    let query = this.client
      .from("section_workflow_revisions")
      .select("*")
      .eq("report_section_id", reportSectionId)
      .order("revision_number", { ascending: true })
      .order("id", { ascending: true })
      .limit(page.limit + 1);
    if (cursor.value) {
      const [number, id] = cursor.value as [number, string];
      query = query.or(`revision_number.gt.${number},and(revision_number.eq.${number},id.gt.${quoteFilter(id)})`);
    }
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Section workflow revisions could not be listed.");
    return createPage(
      (data ?? []) as SectionWorkflowRevisionRow[],
      page,
      order,
      (row) => [row.revision_number, row.id],
      mapSectionWorkflowRevisionRow,
    );
  }

  async create(
    record: Omit<PersistedReport, "revision" | "updatedAt">,
  ): Promise<RepositoryResult<PersistedReport>> {
    const metadata = reportMetadataCodec.decode(record.metadata, record.metadata.schemaVersion);
    if (!metadata.ok) return validationFailure(metadata.error.message);
    const { data, error } = await this.client
      .from("reports")
      .insert({
        id: record.id,
        organisation_id: record.organisationId,
        audit_engagement_id: record.auditEngagementId ?? null,
        report_type: record.reportType,
        title: record.title,
        reporting_period: record.reportingPeriod,
        template_key: record.templateKey ?? null,
        displayed_version_id: record.displayedVersionId ?? null,
        current_section_id: record.currentSectionId ?? null,
        metadata_schema_version: metadata.value.schemaVersion,
        metadata: metadata.value,
        archived_at: record.archivedAt ?? null,
      })
      .select("*")
      .single();
    if (error) return databaseFailure(error, "The report could not be created.");
    return mapReportRow(data as ReportRow);
  }

  async update(
    id: string,
    changes: Partial<Omit<PersistedReport, "id" | "organisationId" | "revision" | "updatedAt">>,
    options: ExpectedRevision,
  ): Promise<RepositoryResult<PersistedReport>> {
    const row: Database["public"]["Tables"]["reports"]["Update"] = {};
    if ("auditEngagementId" in changes) row.audit_engagement_id = changes.auditEngagementId ?? null;
    if (changes.reportType !== undefined) row.report_type = changes.reportType;
    if (changes.title !== undefined) row.title = changes.title;
    if (changes.reportingPeriod !== undefined) row.reporting_period = changes.reportingPeriod;
    if ("templateKey" in changes) row.template_key = changes.templateKey ?? null;
    if ("displayedVersionId" in changes) row.displayed_version_id = changes.displayedVersionId ?? null;
    if ("currentSectionId" in changes) row.current_section_id = changes.currentSectionId ?? null;
    if ("archivedAt" in changes) row.archived_at = changes.archivedAt ?? null;
    if (changes.metadata !== undefined) {
      const metadata = reportMetadataCodec.decode(changes.metadata, changes.metadata.schemaVersion);
      if (!metadata.ok) return validationFailure(metadata.error.message);
      row.metadata_schema_version = metadata.value.schemaVersion;
      row.metadata = metadata.value;
    }
    if (!Object.keys(row).length) return validationFailure("At least one report change is required.");
    const { data, error } = await this.client
      .from("reports")
      .update(row)
      .eq("id", id)
      .eq("revision", options.expectedRevision)
      .select("*")
      .maybeSingle();
    if (error) return databaseFailure(error, "The report could not be updated.");
    if (data) return mapReportRow(data as ReportRow);
    const current = await this.getById(id);
    return current.ok ? { ok: false, error: revisionConflict() } : current;
  }

  async createVersion(
    version: Omit<PersistedReportVersion, "revision" | "updatedAt">,
    _idempotencyKey: string,
  ): Promise<RepositoryResult<PersistedReportVersion>> {
    if (version.status !== "Draft") return validationFailure("A new report version must be editable and start as Draft.");
    const metadata = reportVersionMetadataCodec.decode(version.metadata, version.metadata.schemaVersion);
    if (!metadata.ok) return validationFailure(metadata.error.message);
    const { data, error } = await this.client
      .from("report_versions")
      .insert({
        id: version.id,
        organisation_id: version.organisationId,
        report_id: version.reportId,
        version_number: version.versionNumber,
        status: version.status,
        source_version_id: version.sourceVersionId ?? null,
        content_checksum: version.contentChecksum ?? null,
        presentation_signature: version.presentationSignature ?? null,
        audit_date: version.auditDate ?? null,
        metadata_schema_version: metadata.value.schemaVersion,
        metadata: metadata.value,
      })
      .select("*")
      .single();
    if (error) return databaseFailure(error, "The report version could not be created.");
    return mapReportVersionRow(data as ReportVersionRow);
  }

  async createSections(
    sections: Array<Omit<PersistedReportSection, "revision" | "updatedAt">>,
  ): Promise<RepositoryResult<PersistedReportSection[]>> {
    if (!sections.length) return validationFailure("At least one ordered report section is required.");
    const organisationId = sections[0].organisationId;
    const reportVersionId = sections[0].reportVersionId;
    if (
      sections.some(
        (section) =>
          section.organisationId !== organisationId ||
          section.reportVersionId !== reportVersionId ||
          !Number.isInteger(section.ordinal) ||
          section.ordinal < 1,
      )
    ) return validationFailure("All sections must share one organisation and version and have positive integer ordinals.");
    if (new Set(sections.map((section) => section.ordinal)).size !== sections.length) {
      return validationFailure("Section ordinals must be unique.");
    }
    const { data, error } = await this.client
      .from("report_sections")
      .insert(
        sections.map((section) => ({
          id: section.id,
          organisation_id: section.organisationId,
          report_version_id: section.reportVersionId,
          section_key: section.sectionKey,
          ordinal: section.ordinal,
          heading: section.heading,
          status: section.status,
          current_workflow_revision_id: section.currentWorkflowRevisionId ?? null,
        })),
      )
      .select("*");
    if (error) return databaseFailure(error, "The ordered report sections could not be created.");
    return mapRepositoryRows((data ?? []) as ReportSectionRow[], mapReportSectionRow);
  }

  async appendSectionRevision(
    revision: PersistedSectionRevision,
    expectedSectionRevision: number,
  ): Promise<RepositoryResult<PersistedSectionRevision>> {
    const payload = sectionWorkflowCodec.decode(revision.payload, revision.payload.schemaVersion);
    if (!payload.ok) return validationFailure(payload.error.message);
    if (!isSha256(revision.payloadChecksum)) return validationFailure("Workflow payload checksum must be SHA-256.");
    const { data, error } = await this.client.rpc("append_section_workflow_revision", {
      p_id: revision.id,
      p_organisation_id: revision.organisationId,
      p_report_version_id: revision.reportVersionId,
      p_report_section_id: revision.reportSectionId,
      p_revision_number: revision.revisionNumber,
      p_payload_schema_version: payload.value.schemaVersion,
      p_payload: payload.value,
      p_payload_checksum: revision.payloadChecksum,
      p_source_revision_id: revision.sourceRevisionId ?? (null as unknown as string),
      p_expected_section_revision: expectedSectionRevision,
    });
    if (error) return databaseFailure(error, "The section workflow revision could not be appended.");
    return mapSectionWorkflowRevisionRow(data as SectionWorkflowRevisionRow);
  }

  async updateVersion(
    id: string,
    changes: Partial<
      Pick<PersistedReportVersion, "status" | "contentChecksum" | "presentationSignature" | "auditDate" | "metadata">
    >,
    options: ExpectedRevision,
  ): Promise<RepositoryResult<PersistedReportVersion>> {
    const row: Database["public"]["Tables"]["report_versions"]["Update"] = {};
    if (changes.status !== undefined) row.status = changes.status;
    if ("contentChecksum" in changes) row.content_checksum = changes.contentChecksum ?? null;
    if ("presentationSignature" in changes) row.presentation_signature = changes.presentationSignature ?? null;
    if ("auditDate" in changes) row.audit_date = changes.auditDate ?? null;
    if (changes.metadata !== undefined) {
      const metadata = reportVersionMetadataCodec.decode(changes.metadata, changes.metadata.schemaVersion);
      if (!metadata.ok) return validationFailure(metadata.error.message);
      row.metadata_schema_version = metadata.value.schemaVersion;
      row.metadata = metadata.value;
    }
    if (!Object.keys(row).length) return validationFailure("At least one report-version change is required.");
    const { data, error } = await this.client
      .from("report_versions")
      .update(row)
      .eq("id", id)
      .eq("revision", options.expectedRevision)
      .select("*")
      .maybeSingle();
    if (error) return databaseFailure(error, "The report version could not be updated.");
    if (data) return mapReportVersionRow(data as ReportVersionRow);
    const current = await this.getVersion(id);
    return current.ok ? { ok: false, error: revisionConflict() } : current;
  }

  async cloneEditableVersion(
    command: CloneEditableReportVersionCommand,
  ): Promise<RepositoryResult<PersistedReportVersion>> {
    if (!Number.isInteger(command.newVersionNumber) || command.newVersionNumber < 1) {
      return validationFailure("The new version number must be a positive integer.");
    }
    if (!command.sections.length || new Set(command.sections.map((entry) => entry.sourceSectionId)).size !== command.sections.length) {
      return validationFailure("The clone requires one unique identity mapping for every source section.");
    }
    const { data, error } = await this.client.rpc("clone_editable_report_version", {
      p_organisation_id: command.organisationId,
      p_report_id: command.reportId,
      p_source_version_id: command.sourceVersionId,
      p_new_version_id: command.newVersionId,
      p_new_version_number: command.newVersionNumber,
      p_section_map: command.sections.map((entry) => ({
        sourceSectionId: entry.sourceSectionId,
        newSectionId: entry.newSectionId,
        ...(entry.newWorkflowRevisionId ? { newWorkflowRevisionId: entry.newWorkflowRevisionId } : {}),
      })),
      p_expected_report_revision: command.expectedReportRevision,
    });
    if (error) return databaseFailure(error, "The editable report version could not be cloned.");
    return mapReportVersionRow(data as ReportVersionRow);
  }
}
